import React, { useState, useEffect, useContext } from 'react';
import {
  Alert,
  Grid,
  TextField,
  Tooltip,
  styled,
} from '@nextiva/next-ui-components';
import { useQueryClient } from 'react-query';
import { isValidEmail } from 'utils/validators';
import { ChimeService } from 'services';
import {
  UserContext,
  actionTypes as userActionTypes,
} from 'containers/pages/ManageUsers/usersContext';
import { ChimeContext, DispatchChimeContext, actionTypes } from '../context';
import Footer from './footer';

const StyledTextfield = styled(TextField)`
  input {
    color: ${({ error, theme }) => error && theme.palette.warning.contrastText};
  }
`;

const StyledAlert = styled(Alert)`
  background-color: ${({ theme }) => theme.palette.orange[50]};
`;

const CoSpaceFooter = () => {
  return (
    <div className="m-chime-drawer__footer-banner">
      <div className="content">
        <span className="cospace-logo" />
        <span className="message">
          Video chat, screen sharing, and chat. All in one place.&nbsp;
          <a href="/#" data-testid="PlatformAdminUI-Step2-Launch">
            Launch Cospace
          </a>
        </span>
      </div>
    </div>
  );
};

const FieldLabel = () => {
  return (
    <>
      User’s Amazon id
      <Tooltip
        placement="right"
        title="The Amazon ID is the same as the username used when logging into amazon. It’s most likely the users email address."
      >
        <span className="tooltip-icon" role="presentation" />
      </Tooltip>
    </>
  );
};

const Step2 = () => {
  const {
    chimeSettings: { activeUser, isEdit },
  } = useContext(ChimeContext);
  const { dispatch } = useContext(UserContext);

  const chimeDispatch = useContext(DispatchChimeContext);

  const setStep = (step) => {
    chimeDispatch({
      type: actionTypes.SET_CHIME_DRAWER_STEP,
      step,
    });
  };

  const updateUserAmazonId = (email, amazonId) => {
    dispatch({
      type: userActionTypes.UPDATE_USER_AMAZONID,
      amazonId,
      email,
    });
  };

  const setDrawerStatus = (state) => {
    chimeDispatch({
      type: actionTypes.SET_CHIME_DRAWER_STATUS,
      state,
    });
  };

  const closeHandler = () => {
    setDrawerStatus(false);
  };

  const [askRemove, setAskRemove] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const [amazonId, setAmazonId] = useState('');
  const [saveText, setSaveText] = useState('Create');
  const [chimeError, setChimeError] = useState('');
  const queryClient = useQueryClient();

  useEffect(() => {
    if (chimeError) {
      const timer = setTimeout(() => {
        setChimeError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [chimeError]);

  const AskRemove = () => {
    const cancel = () => {
      setAskRemove(false);
    };

    const remove = () => {
      ChimeService.deleteAmazonId(activeUser.email)
        .then(() => {
          setAskRemove(false);
          updateUserAmazonId(activeUser.email, '');
          closeHandler();
          queryClient.invalidateQueries(['getUserList']);
        })
        .catch((err) => {
          setChimeError(err?.response?.errors?.msg);
        });
    };

    return (
      <div className="nui-m-base__error-banner m-chime-drawer__content__ask-remove">
        <Grid container>
          <Grid item xs={12}>
            <span>
              Are you sure you want to remove the Chime license? <br />
              <br />
              Doing so will delete the Amazon ID, and disable the user
              <br />
              from using Chime.
            </span>
          </Grid>
          <Grid container className="action-bar">
            <Grid item xs={2}>
              <span
                className="action"
                role="presentation"
                onClick={cancel}
                data-testid="PlatformAdminUI-Step2-Cancel"
              >
                Cancel
              </span>
            </Grid>
            <Grid item xs={10}>
              <span
                className="action"
                role="presentation"
                onClick={remove}
                data-testid="PlatformAdminUI-Step2-Yes"
              >
                Yes, Remove Chime License
              </span>
            </Grid>
          </Grid>
        </Grid>
      </div>
    );
  };

  const promptAskRemove = () => {
    setAskRemove(true);
  };

  const setPostHandler = (e) => {
    const { value } = e.target;
    setChimeError('');
    setAmazonId(value);
    setIsValid(isValidEmail(value));
  };

  const stepHandler = () => {
    if (isEdit) {
      ChimeService.updateAmazonId(activeUser.email, {
        amazon_user_id: amazonId,
      })
        .then(() => {
          setChimeError('');
          updateUserAmazonId(activeUser.email, amazonId);
          closeHandler();
        })
        .catch(() => {
          setChimeError(
            'There was an issue with the Amazon user ID. Please verify the user ID and try again.',
          );
        });
    } else {
      ChimeService.createAmazonId({
        platform_login_id: activeUser.email,
        amazon_user_id: amazonId,
      })
        .then(() => {
          setChimeError('');
          setStep(3);
          updateUserAmazonId(activeUser.email, amazonId);
          queryClient.invalidateQueries(['getUserList']);
        })
        .catch(() => {
          setChimeError(
            'There was an issue with the Amazon user ID. Please verify the user ID and try again.',
          );
        });
    }
  };

  useEffect(() => {
    if (isEdit) {
      setSaveText('Save');
    } else {
      setSaveText('Create');
    }
  }, [isEdit]);

  useEffect(() => {
    const { profiles } = activeUser;
    if (
      profiles[0]?.amazonAccount &&
      (profiles[0].amazonAccount.primary_identifier ||
        profiles[0].amazonAccount.PrimaryIdentifier)
    ) {
      setAmazonId(
        profiles[0].amazonAccount.primary_identifier ||
          profiles[0].amazonAccount.PrimaryIdentifier,
      );
    }
  }, [activeUser]);

  return (
    <>
      <div className="m-chime-drawer__content">
        <Grid container spacing={2} className="header">
          {chimeError && (
            <StyledAlert
              severity="error"
              fullWidth
              data-testid="PlatformAdminUI-ManageUsers-ChimeDrawer-Alert"
            >
              {chimeError}
            </StyledAlert>
          )}
          <Grid item xs={12}>
            <span className="chime-logo" />
          </Grid>
          <Grid item xs={12} className="description">
            <span className="Heading5">
              Set up Chime by adding your users Amazon ID below.
            </span>
          </Grid>
          <Grid item xs={12}>
            <StyledTextfield
              fullWidth
              id="amazonId"
              label={<FieldLabel />}
              error={chimeError}
              onChange={setPostHandler}
              value={amazonId}
              data-testid="PlatformAdminUI-Step2-TextField"
            />
            {!askRemove && isEdit && (
              <span
                className="remove-link"
                role="presentation"
                onClick={promptAskRemove}
                data-testid="PlatformAdminUI-Step2-RemoveLink"
              >
                Remove Chime license from this user
              </span>
            )}
            {askRemove && <AskRemove />}
          </Grid>
        </Grid>
      </div>
      {isEdit && <CoSpaceFooter />}
      <Footer
        cancelOnClick={closeHandler}
        saveText={saveText}
        saveOnClick={stepHandler}
        saveEnabled={isValid}
      />
    </>
  );
};

export default Step2;
